import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { CancelToken, isCancel } from 'axios';
import flatten from 'lodash/flatten';
import { ExportToCsv } from 'export-to-csv';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';

import { EXPORT_DM1_CSV_DATA } from '../../Constants/TrackerClasses';
import CurrentOrgContext from '../../Context/CurrentOrgContext';
import OrgsMapContext from '../../Context/OrgsMapContext';
import RequestHelper from '../../Lib/AlertAndDmRequestHelper';
import AlertUtils from '../../Lib/AlertUtils';
import Dm1Utils from '../../Lib/Dm1Utils';
import AlertService from '../../Services/AlertService';
import DM1Service from '../../Services/DM1Service';
import AssetService from '../../Services/AssetService';

const LIMIT = 100;

function ActiveAlertsBanner(props) {
  const {
    assetView,
    clearRequestPending,
    gatewayIdFilter,
    includeChildren,
    orgIdFilter,
    setError,
    tab,
  } = props;
  const currentOrg = useContext(CurrentOrgContext),
    orgsMap = useContext(OrgsMapContext);

  const alertService = useMemo(() => new AlertService(), []),
    alertUtils = useMemo(() => new AlertUtils(), []),
    assetService = useMemo(() => new AssetService(), []),
    dm1Service = useMemo(() => new DM1Service(), []),
    dm1Utils = useMemo(() => new Dm1Utils(), []),
    orgID = useMemo(
      () => orgIdFilter || currentOrg?._id,
      [currentOrg?._id, orgIdFilter]
    ),
    service = useMemo(
      () => (tab === 'alerts' ? alertService : dm1Service),
      [alertService, dm1Service, tab]
    ),
    source = useMemo(() => CancelToken.source(), []),
    utils = useMemo(
      () => (tab === 'alerts' ? alertUtils : dm1Utils),
      [alertUtils, dm1Utils, tab]
    );

  const [activeAlerts, setActiveAlerts] = useState(0),
    [activeAssets, setActiveAssets] = useState(0),
    [csvExportPending, updateCsvExportPending] = useState(false),
    [totalAssets, setTotalAssets] = useState(0);

  const fetchActiveAlerts = useCallback(async () => {
    try {
      const conditionalFilter = gatewayIdFilter ? { gatewayIdFilter } : {};
      const { ActiveAssets, Count } = await service[
        tab === 'alerts' ? 'fetchAlertByRuleMetrics' : 'getDM1FaultsCount'
      ](
        {
          activeAssets: true,
          children: includeChildren,
          orgID,
          orgIdFilter: orgID,
          severityFilter: 'critical',
          transitionFilter: 'positive',
          ...conditionalFilter,
        },
        source.token
      );
      setActiveAlerts(Count || 0);
      setActiveAssets(ActiveAssets || 0);
    } catch (err) {
      if (isCancel(err)) return;
      setError(err.response?.data?.message ?? err.message);
    }
  }, [
    gatewayIdFilter,
    includeChildren,
    orgID,
    service,
    setError,
    source.token,
    tab,
  ]);

  const fetchAssetCountForOrg = useCallback(async () => {
    try {
      const { count } = await assetService.searchAssets(
        { children: includeChildren, limit: 0, orgID },
        source.token
      );
      setTotalAssets(count || 0);
    } catch (err) {
      if (isCancel(err)) return;
      setError(err.response?.data?.message ?? err.message);
    }
  }, [assetService, includeChildren, orgID, setError, source.token]);

  const fetchCsvData = async () => {
    try {
      const request = new RequestHelper({
        ...props,
        sort: props.sort.by,
        order: props.sort.direction,
        orgID,
        orgIdFilter: orgID,
        typeFilter: tab === 'alerts' ? 'rule' : null,
      });
      const { Count } = await request[
        `fetch${tab === 'alerts' ? 'Alert' : 'DM1'}Metrics`
      ](source.token);
      if (Count) {
        const requestCount = Math.ceil(Count / LIMIT);
        const alertRequests = Array(requestCount)
          .fill(0)
          .map((r, index) => {
            return new RequestHelper({
              ...props,
              sort: props.sort.by,
              order: props.sort.direction,
              orgID,
              orgIdFilter: orgID,
              typeFilter: tab === 'alerts' ? 'rule' : null,
              limit: LIMIT,
              page: index + 1,
            })[`fetch${tab === 'alerts' ? 'Alerts' : 'DM1s'}`](source.token);
          });
        const responses = await Promise.all(alertRequests);
        return responses.reduce((data, r) => {
          data.push(...r);
          return data;
        }, []);
      }
      return [];
    } catch (err) {
      if (!isCancel(err)) {
        setError(err.response?.data?.message ?? err.message);
      }
      return [];
    }
  };

  const exportCsv = async () => {
    updateCsvExportPending(true);
    const rawData = await fetchCsvData();
    if (!rawData.length) return updateCsvExportPending(false);

    const utilsMethod = `format${tab === 'alerts' ? 'Alert' : 'Dm1'}ForCsv`;

    const data = flatten(rawData.map((d) => utils[utilsMethod](d, orgsMap)));
    const fileName = utils.generateCsvFilename();
    const csvExporter = new ExportToCsv({
      filename: fileName,
      title: fileName,
      useKeysAsHeaders: true,
    });
    csvExporter.generateCsv(data);
    updateCsvExportPending(false);
  };

  useEffect(() => {
    if (clearRequestPending === true) return;
    fetchActiveAlerts();
  }, [clearRequestPending, fetchActiveAlerts]);

  useEffect(() => {
    if (!orgID) return;
    fetchAssetCountForOrg();
  }, [fetchAssetCountForOrg, orgID]);

  return (
    <div
      className="d-flex justify-content-around justify-content-lg-start align-items-center ml-lg-3 row bg-white rounded mb-3 mb-lg-0"
      style={{ height: '4rem' }}
    >
      <div className="d-flex flex-column text-center text-lg-left">
        <span className="text-danger large-text">{activeAlerts}</span>
        <span className="text-muted three-quarter-text">
          {tab === 'alerts' ? 'Active Alerts' : 'Active DM1 Faults'}
        </span>
      </div>
      <div
        className={
          assetView
            ? 'd-none'
            : 'd-flex flex-column text-center text-lg-left ml-lg-5'
        }
      >
        <div>
          <span className="text-danger large-text">{activeAssets}</span>
          <span className="text-icon-link-gray three-quarter-text ml-2">{`/ ${totalAssets}`}</span>
        </div>
        <span className="text-muted three-quarter-text">Assets affected</span>
      </div>
      <div className="d-none d-lg-flex align-items-center ml-lg-5">
        <button
          className={`btn btn-sm modal-button-primary nine-tenth-text ${EXPORT_DM1_CSV_DATA}`}
          disabled={csvExportPending}
          onClick={exportCsv}
        >
          {csvExportPending ? (
            <FontAwesomeIcon icon={faCircleNotch} spin />
          ) : (
            'Export Data'
          )}
        </button>
      </div>
      <style jsx>{`
        .large-text {
          font-size: 1.25rem;
        }
      `}</style>
    </div>
  );
}

ActiveAlertsBanner.propTypes = {
  assetView: PropTypes.bool,
  clearRequestPending: PropTypes.bool,
  dmExport: PropTypes.shape({
    isLoaded: PropTypes.bool,
    data: PropTypes.arrayOf(PropTypes.object),
    fileName: PropTypes.string,
  }),
  fmiFilter: PropTypes.number,
  gatewayIdFilter: PropTypes.string,
  includeChildren: PropTypes.bool,
  orgID: PropTypes.string,
  orgIdFilter: PropTypes.string,
  ruleIdFilter: PropTypes.string,
  saFIlter: PropTypes.number,
  setError: PropTypes.func,
  sort: PropTypes.shape({
    by: PropTypes.oneOf(['TriggeredAt', 'DetectedAt']),
    direction: PropTypes.oneOf(['ASC', 'DESC']),
  }),
  spnFilter: PropTypes.number,
  tab: PropTypes.oneOf(['alerts', 'dm1']),
};

export default memo(ActiveAlertsBanner);
